import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Lead } from '../../../../types'
import { LeadEditFormData, LeadEditFormattedValues } from '../../domain/interfaces'
import { GetLeadUseCase, UpdateLeadUseCase, CheckPhoneUseCase, CheckEmailUseCase, CheckDocumentUseCase } from '../../application/use-cases'
import { LeadRepository } from '../../infrastructure/repositories'
import { LeadMapper } from '../../application/mappers'
import { apiService } from '../../../../services/api'

const leadRepository = new LeadRepository()
const getLeadUseCase = new GetLeadUseCase(leadRepository)
const updateLeadUseCase = new UpdateLeadUseCase(leadRepository)
const checkPhoneUseCase = new CheckPhoneUseCase(leadRepository)
const checkEmailUseCase = new CheckEmailUseCase(leadRepository)
const checkDocumentUseCase = new CheckDocumentUseCase(leadRepository)

export const useLeadEdit = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const isCreating = !id

  const [lead, setLead] = useState<Lead | null>(null)
  const [isLoading, setIsLoading] = useState(!isCreating)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [checkPhoneWarning, setCheckPhoneWarning] = useState<string | null>(null)
  const [checkEmailWarning, setCheckEmailWarning] = useState<string | null>(null)
  const [checkDocumentWarning, setCheckDocumentWarning] = useState<string | null>(null)
  const [showVehicleSelection, setShowVehicleSelection] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [duplicateLeadId, setDuplicateLeadId] = useState<string | null>(null)
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false)
  const [isCheckingPhone, setIsCheckingPhone] = useState(false)
  const [isCheckingDocument, setIsCheckingDocument] = useState(false)

  const [formData, setFormData] = useState<LeadEditFormData>({
    name: '',
    phones: [],
    emails: [],
    document: '',
    cnh: '',
    message: '',
    source: '',
    sellerId: '',
    vehicleInterests: [],
    interest: {},
    notes: '',
    contactCount: null,
    engagement: '',
    purchaseIntent: '',
    usageType: '',
    vehicleMoment: '',
    hasTradeIn: false,
    tradeInVehicleDescription: '',
    tradeInVehicles: [],
    tradeInEvaluationEligible: false,
    tradeInEvaluationScore: null,
    tradeInEstimatedValue: null,
    tradeInEvaluationNotes: '',
    wantsFinancing: false,
    financingWithTradeIn: false,
    downPaymentAmount: null,
    downPaymentEntries: [],
    desiredInstallmentAmount: null,
    approvedCreditAmount: null,
    creditScore: null,
    financingNotes: ''
  })

  const [formattedValues, setFormattedValues] = useState<LeadEditFormattedValues>({
    minPriceFormatted: '',
    maxPriceFormatted: '',
    minMileageFormatted: '',
    maxMileageFormatted: ''
  })

  const fetchLead = useCallback(async () => {
    if (!id || isCreating) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const data = await getLeadUseCase.execute(id)
      setLead(data)
    } catch (err) {
      console.error('Erro ao carregar lead:', err)
      setError('Erro ao carregar lead. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }, [id, isCreating])

  useEffect(() => {
    fetchLead()
  }, [fetchLead])

  useEffect(() => {
    if (lead) {
      setFormData(LeadMapper.toFormData(lead))
      setFormattedValues(LeadMapper.toFormattedValues(lead))
    }
  }, [lead])

  const checkPhoneExists = useCallback(
    async (phone: string) => {
      const currentLeadPhones = lead?.phones?.map(p => p.phone) || []
      setIsCheckingPhone(true)
      try {
        const result = await checkPhoneUseCase.execute(phone, currentLeadPhones)
        setCheckPhoneWarning(result.warning)

        if (result.exists && result.leadId) {
          setDuplicateLeadId(result.leadId)
          setShowDuplicatePopup(true)
        }
      } finally {
        setIsCheckingPhone(false)
      }
    },
    [lead]
  )

  useEffect(() => {
    const primary = formData.phones.find(p => p.isPrimary)
    if (primary?.ddd && primary?.phone) {
      const fullPhone = `${primary.ddi?.replace('+', '') || '55'}${primary.ddd.replace(/\D/g, '')}${primary.phone.replace(/\D/g, '')}`
      const timer = setTimeout(() => {
        checkPhoneExists(fullPhone)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [formData.phones, checkPhoneExists])

  const checkEmailExists = useCallback(
    async (email: string) => {
      const currentLeadEmails = lead?.emails?.map(e => e.email) || []
      const result = await checkEmailUseCase.execute(email, currentLeadEmails)
      setCheckEmailWarning(result.warning)
    },
    [lead]
  )

  useEffect(() => {
    const primaryEmail = formData.emails.find(e => e.isPrimary)?.email
    if (primaryEmail) {
      const timer = setTimeout(() => {
        checkEmailExists(primaryEmail)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [formData.emails, checkEmailExists])

  const checkDocumentExists = useCallback(
    async (document: string) => {
      const currentLeadDocument = lead?.document || ''
      setIsCheckingDocument(true)
      try {
        const result = await checkDocumentUseCase.execute(document, currentLeadDocument)

        if (result.exists && result.leadName && result.documentType) {
          const key = result.documentType === 'cpf' ? 'leads.form.documentDuplicateCPF' : 'leads.form.documentDuplicateCNPJ'
          const warningMessage = t(key, { name: result.leadName })
          setCheckDocumentWarning(warningMessage)
        } else {
          setCheckDocumentWarning(null)
        }

        if (result.exists && result.leadId) {
          setDuplicateLeadId(result.leadId)
          setShowDuplicatePopup(true)
        }
      } finally {
        setIsCheckingDocument(false)
      }
    },
    [lead, t]
  )

  useEffect(() => {
    if (formData.document) {
      const timer = setTimeout(() => {
        checkDocumentExists(formData.document)
      }, 500)
      return () => clearTimeout(timer)
    } else {
      setCheckDocumentWarning(null)
    }
  }, [formData.document, checkDocumentExists])

  const validate = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório'
    }

    const validPhones = formData.phones.filter(p => {
      const ddd = p.ddd?.replace(/\D/g, '') || ''
      const phone = p.phone?.replace(/\D/g, '') || ''
      return ddd.length === 2 && (phone.length === 8 || phone.length === 9)
    })
    if (validPhones.length === 0) {
      errors.phones = 'Pelo menos um telefone válido é obrigatório'
    }

    const validEmails = formData.emails.filter(e => e.email && e.email.trim())
    const invalidEmail = validEmails.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.email))
    if (invalidEmail) {
      errors.emails = 'Um ou mais e-mails são inválidos'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const onNextStepValidation = (currentStep: number, targetStep: number): boolean => {
    if (currentStep === 0 && targetStep > 0) {
      const errors: Record<string, string> = {}

      if (!formData.name.trim()) {
        errors.name = 'Nome é obrigatório'
      }

      const validPhones = formData.phones.filter(p => {
      const ddd = p.ddd?.replace(/\D/g, '') || ''
      const phone = p.phone?.replace(/\D/g, '') || ''
      return ddd.length === 2 && (phone.length === 8 || phone.length === 9)
    })
      if (validPhones.length === 0) {
        errors.phones = 'Pelo menos um telefone válido é obrigatório'
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors)
        return false
      }

      setValidationErrors({})
      return true
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validate()) {
      return {
        success: false,
        message: 'Preencha os campos obrigatórios: nome e telefone'
      }
    }

    setIsSubmitting(true)
    try {
      const apiData = LeadMapper.toApiData(formData, formattedValues)

      if (isCreating) {
        const primary = formData.phones.find(p => p.isPrimary) || formData.phones[0]
        const primaryPhone = primary?.ddd && primary?.phone
          ? `${primary.ddi?.replace('+', '') || '55'}${primary.ddd.replace(/\D/g, '')}${primary.phone.replace(/\D/g, '')}`
          : undefined
        const primaryEmail = formData.emails.find(e => e.isPrimary)?.email || formData.emails[0]?.email

        let duplicateLeadId: string | undefined
        if (primaryPhone) {
          const phoneResult = await checkPhoneUseCase.execute(primaryPhone)
          if (phoneResult.exists) {
            duplicateLeadId = phoneResult.leadId
          }
        }
        if (!duplicateLeadId && primaryEmail) {
          const emailResult = await checkEmailUseCase.execute(primaryEmail)
          if (emailResult.exists) {
            duplicateLeadId = emailResult.leadId
          }
        }

        if (duplicateLeadId) {
          setDuplicateLeadId(duplicateLeadId)
          setShowDuplicatePopup(true)
          return { success: false, message: 'Cliente duplicado encontrado' }
        }
      }

      if (isCreating) {
        const newLead = await apiService.createLead(apiData)
        setTimeout(() => {
          navigate(`/leads/edit/${newLead.id}`)
        }, 1500)
        return { success: true, message: 'Lead criado com sucesso!' }
      } else {
        if (!id) return { success: false, message: 'ID do lead não encontrado' }
        await updateLeadUseCase.execute(id, apiData)
        setTimeout(() => {
          navigate('/leads')
        }, 1500)
        return { success: true, message: 'Lead atualizado com sucesso!' }
      }
    } catch (err: any) {
      console.error(`Erro ao ${isCreating ? 'criar' : 'atualizar'} lead:`, err)
      return {
        success: false,
        message: err.message || `Erro ao ${isCreating ? 'criar' : 'atualizar'} lead. Tente novamente.`
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/leads')
  }

  const clearDuplicateData = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.map(p => ({ ...p, phone: '' })),
      emails: prev.emails.map(e => ({ ...e, email: '' }))
    }))
    setDuplicateLeadId(null)
    setShowDuplicatePopup(false)
  }, [])

  const updateFormData = (updates: Partial<LeadEditFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear validation errors for updated fields
    const updatedKeys = Object.keys(updates)
    if (updatedKeys.some(k => validationErrors[k])) {
      setValidationErrors(prev => {
        const next = { ...prev }
        updatedKeys.forEach(k => delete next[k])
        return next
      })
    }
  }

  const updateFormattedValues = (updates: Partial<LeadEditFormattedValues>) => {
    setFormattedValues(prev => ({ ...prev, ...updates }))
  }

  return {
    lead,
    formData,
    formattedValues,
    isLoading,
    error,
    isSubmitting,
    checkPhoneWarning,
    isCheckingPhone,
    checkEmailWarning,
    checkDocumentWarning,
    isCheckingDocument,
    showVehicleSelection,
    setShowVehicleSelection,
    handleSubmit,
    handleCancel,
    updateFormData,
    updateFormattedValues,
    validationErrors,
    id,
    isCreating,
    duplicateLeadId,
    setDuplicateLeadId,
    showDuplicatePopup,
    setShowDuplicatePopup,
    onNextStepValidation,
    clearDuplicateData,
  }
}
